<div class="w-full max-w-6xl mx-auto p-6">
  @if (error()) {
    <div class="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-center gap-2">
      <i class="pi pi-exclamation-triangle text-red-600"></i>
      <p class="text-red-800">{{ error() }}</p>
    </div>
  }

  <form [formGroup]="planForm" (ngSubmit)="$event.preventDefault()">
    <p-card styleClass="sale-form-card">
      <ng-template pTemplate="header">
        <div class="px-4 py-3 border-b border-slate-200 bg-slate-50/50">
          <div class="flex items-center gap-3">
            <p-button
              icon="pi pi-arrow-left"
              [rounded]="true"
              [text]="true"
              severity="secondary"
              type="button"
              (onClick)="cancel()"
              pTooltip="Volver al listado"
              tooltipPosition="bottom"
            />
            <h2 class="text-lg font-semibold text-slate-900 flex items-center gap-2 min-w-0">
              <span class="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-sky-100 text-sky-600">
                <i class="pi pi-box"></i>
              </span>
              <span class="truncate">{{ isEditMode() ? 'Editar plan' : 'Nuevo plan' }}</span>
            </h2>
          </div>
        </div>
        <div class="flex flex-wrap items-center justify-end gap-2 border-b border-slate-200 bg-slate-50/90 px-3 py-2">
          <div class="inline-flex items-baseline gap-x-1.5 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5">
            <span class="text-[11px] font-semibold tracking-wider text-slate-500">STATUS:</span>
            <span class="text-sm font-medium text-slate-900 font-mono">{{ statusDisplayLabel() }}</span>
            <i
              class="pi pi-question-circle text-slate-400 cursor-help text-xs shrink-0"
              [pTooltip]="statusTooltip"
              tooltipPosition="bottom"
            ></i>
          </div>
        </div>
      </ng-template>
      <p-tabs [value]="activeTab()" (valueChange)="activeTab.set($event)">
        <p-tablist>
          <p-tab value="general" class="flex items-center gap-2! text-inherit">
            <i class="pi pi-receipt"></i>
            <span>General</span>
          </p-tab>
          <p-tab
            value="auditoria"
            [disabled]="!editingPlanId()"
            class="flex items-center gap-2! text-inherit"
            [class.opacity-50]="!editingPlanId()"
            [class.pointer-events-none]="!editingPlanId()"
          >
            <i class="pi pi-history"></i>
            <span>Auditoría</span>
          </p-tab>
        </p-tablist>
        <p-tabpanels>
          <p-tabpanel value="general">
      <!-- Datos del plan -->
      <div class="mb-8">
        <h3 class="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
          <i class="pi pi-box"></i>
          Datos del plan
        </h3>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div class="flex flex-col gap-1 md:col-span-2">
            <p-floatlabel class="w-full" variant="on">
              <input
                pInputText
                id="planName"
                formControlName="name"
                [maxlength]="150"
                fluid
                autocomplete="off"
                [invalid]="(planForm.get('name')?.invalid ?? false) && (planForm.get('name')?.touched ?? false)"
              />
              <label for="planName">Nombre del plan <span class="text-red-500">*</span></label>
            </p-floatlabel>
            @if (planForm.get('name')?.hasError('required') && planForm.get('name')?.touched) {
              <small class="text-red-600">El nombre es obligatorio.</small>
            } @else if (planForm.get('name')?.hasError('maxlength')) {
              <small class="text-red-600">Máximo 150 caracteres.</small>
            } @else {
              <small class="text-slate-500">Nombre identificador del plan.</small>
            }
          </div>

          <div class="flex flex-col gap-1 md:col-span-2">
            <p-floatlabel class="w-full" variant="on">
              <textarea
                pTextarea
                id="planDescription"
                formControlName="description"
                rows="3"
                [maxlength]="500"
                fluid
                autocomplete="off"
                placeholder="Descripción del plan"
              ></textarea>
              <label for="planDescription">Descripción</label>
            </p-floatlabel>
            @if (planForm.get('description')?.hasError('maxlength')) {
              <small class="text-red-600">Máximo 500 caracteres.</small>
            } @else {
              <small class="text-slate-500">Descripción opcional del plan.</small>
            }
          </div>

          <div class="flex flex-col gap-1">
            <p-floatlabel class="w-full" variant="on">
              <p-select
                formControlName="currencyId"
                inputId="planCurrency"
                [options]="currencySelectOptions()"
                optionLabel="label"
                optionValue="id"
                class="w-full"
                filter
                [invalid]="(planForm.get('currencyId')?.invalid ?? false) && (planForm.get('currencyId')?.touched ?? false)"
              />
              <label for="planCurrency">Moneda <span class="text-red-500">*</span></label>
            </p-floatlabel>
            @if (planForm.get('currencyId')?.hasError('required') && planForm.get('currencyId')?.touched) {
              <small class="text-red-600">La moneda es obligatoria.</small>
            } @else {
              <small class="text-slate-500">Moneda del plan.</small>
            }
          </div>

          <div class="flex flex-col gap-1">
            <p-floatlabel class="w-full" variant="on">
              <p-select
                formControlName="frequency"
                inputId="planFrequency"
                [options]="frequencyOptions"
                optionLabel="label"
                optionValue="value"
                class="w-full"
                [invalid]="(planForm.get('frequency')?.invalid ?? false) && (planForm.get('frequency')?.touched ?? false)"
              />
              <label for="planFrequency">Frecuencia <span class="text-red-500">*</span></label>
            </p-floatlabel>
            <small class="text-slate-500">Periodicidad de cobro del plan.</small>
          </div>

          <div class="flex flex-col gap-1">
            <p-floatlabel class="w-full" variant="on">
              <p-select
                formControlName="status"
                inputId="planStatus"
                [options]="statusOptions"
                optionLabel="label"
                optionValue="value"
                class="w-full"
              />
              <label for="planStatus">Estado <span class="text-red-500">*</span></label>
            </p-floatlabel>
            <small class="text-slate-500">Estado actual del plan.</small>
          </div>

          <div class="flex flex-col gap-1">
            <p-floatlabel class="w-full" variant="on">
              <p-inputNumber
                id="planDiscount"
                formControlName="discount"
                [min]="0"
                mode="currency"
                currency="USD"
                locale="en-US"
                [minFractionDigits]="0"
                [maxFractionDigits]="2"
                fluid
                class="[&_input]:text-right"
              />
              <label for="planDiscount">Descuento</label>
            </p-floatlabel>
            <small class="text-slate-500">Descuento global del plan.</small>
          </div>
        </div>
      </div>

      <!-- Items del plan -->
      <div class="mt-8 border-t border-slate-200 pt-6">
        <div class="flex items-center justify-between mb-4">
          <div>
            <h3 class="text-lg font-semibold text-slate-900 flex items-center gap-2">
              <i class="pi pi-list"></i>
              Items del plan
            </h3>
            <p class="text-sm text-slate-500">Agrega productos o servicios al plan.</p>
          </div>
          <p-button label="Agregar item" icon="pi pi-plus" (onClick)="openLineModal('create')" type="button" />
        </div>

        <div class="bg-white rounded-lg shadow-sm border border-slate-200">
          <div class="overflow-x-scroll">
            <table class="w-full">
              <thead class="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th class="w-10 px-2 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider"></th>
                  <th class="px-6 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">Producto</th>
                  <th class="px-6 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">Cantidad</th>
                  <th class="px-6 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">P. unitario</th>
                  <th class="px-6 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">Subtotal</th>
                  <th class="px-6 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">Impuestos</th>
                  <th class="px-6 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">Total</th>
                  <th class="px-6 py-3 text-right text-xs font-semibold text-slate-700 uppercase tracking-wider">Acciones</th>
                </tr>
              </thead>
              <tbody class="bg-white divide-y divide-slate-200">
                @if (items().length === 0) {
                  <tr>
                    <td class="px-6 py-6 text-center text-sm text-slate-500" colspan="8">
                      No hay items. Haz clic en "Agregar item" para comenzar.
                    </td>
                  </tr>
                }
                @for (item of items(); track item.tempId || item.id || $index) {
                  <tr class="hover:bg-slate-50 transition-colors">
                    <td class="w-10 px-2 py-4 align-top">
                      @if (item.taxes?.length) {
                        <button
                          type="button"
                          class="p-1.5 rounded text-slate-500 hover:bg-slate-200 hover:text-slate-700 transition-colors"
                          (click)="toggleLineExpanded($index)"
                        >
                          <i class="pi text-sm" [class.pi-chevron-right]="!isLineExpanded($index)" [class.pi-chevron-down]="isLineExpanded($index)"></i>
                        </button>
                      }
                    </td>
                    <td class="px-0 py-4 text-sm text-slate-600 max-w-xs truncate">{{ item.description }}</td>
                    <td class="px-6 py-4 whitespace-nowrap text-sm text-slate-600">{{ item.quantity }}</td>
                    <td class="px-6 py-4 whitespace-nowrap text-sm text-slate-600 text-right">{{ item.unitPrice | currency:'USD':'symbol':'1.2-2' }}</td>
                    <td class="px-6 py-4 whitespace-nowrap text-sm text-slate-600 text-right">{{ item.subtotal | currency:'USD':'symbol':'1.2-2' }}</td>
                    <td class="px-6 py-4 whitespace-nowrap text-sm text-slate-600 text-right">{{ item.taxAmount | currency:'USD':'symbol':'1.2-2' }}</td>
                    <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-slate-900 text-right">{{ item.totalAmount | currency:'USD':'symbol':'1.2-2' }}</td>
                    <td class="px-6 py-4 whitespace-nowrap text-right">
                      <div class="flex justify-end gap-1">
                        <p-button icon="pi pi-pen-to-square" [rounded]="true" [text]="true" severity="secondary" (onClick)="openLineModal('edit', $index)" type="button" />
                        <p-button icon="pi pi-trash" [rounded]="true" [text]="true" severity="danger" (onClick)="confirmRemoveLine($index)" type="button" [loading]="lineRemoving()" />
                      </div>
                    </td>
                  </tr>
                  @if (isLineExpanded($index) && item.taxes?.length) {
                    <tr class="bg-slate-50/70">
                      <td colspan="8" class="px-6 py-3">
                        <div class="pl-8 text-sm">
                          <div class="font-medium text-slate-600 mb-2">Desglose de impuestos</div>
                          <table class="w-full max-w-md border border-slate-200 rounded-lg overflow-hidden">
                            <thead>
                              <tr class="bg-slate-100 border-b border-slate-200">
                                <th class="px-3 py-2 text-left text-xs font-medium text-slate-600">Código</th>
                                <th class="px-3 py-2 text-left text-xs font-medium text-slate-600">Tasa</th>
                                <th class="px-3 py-2 text-left text-xs font-medium text-slate-600 min-w-[5rem]">Tipo</th>
                                <th class="px-3 py-2 text-right text-xs font-medium text-slate-600">Base</th>
                                <th class="px-3 py-2 text-right text-xs font-medium text-slate-600">Monto</th>
                              </tr>
                            </thead>
                            <tbody>
                              @for (t of item.taxes; track $index) {
                                <tr class="border-b border-slate-100 last:border-0">
                                  <td class="px-3 py-2 text-slate-700">{{ t.taxCode || '-' }}</td>
                                  <td class="px-3 py-2 text-slate-700">{{ formatTaxRate(t.taxRate) }}</td>
                                  <td class="px-3 py-2 text-slate-700">{{ getTaxCollectionTypeLabel(t.collectionType) }}</td>
                                  <td class="px-3 py-2 text-slate-700 text-right">{{ t.taxBase != null ? (t.taxBase | currency:'USD':'symbol':'1.2-2') : '-' }}</td>
                                  <td class="px-3 py-2 text-slate-700 text-right">{{ t.taxAmount != null ? (t.taxAmount | currency:'USD':'symbol':'1.2-2') : '-' }}</td>
                                </tr>
                              }
                            </tbody>
                          </table>
                        </div>
                      </td>
                    </tr>
                  }
                }
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- Totales -->
      <div class="hidden">
        <input type="number" formControlName="subtotal" />
        <input type="number" formControlName="tax" />
        <input type="number" formControlName="total" />
      </div>

      <div class="mt-8 border-t border-slate-200 pt-6">
        <div class="p-4 bg-slate-50 rounded-lg border border-slate-200 max-w-sm ml-auto">
          <h4 class="text-sm font-semibold text-slate-700 mb-3">Totales</h4>
          <div class="space-y-2 text-sm">
            <div class="flex justify-between text-slate-600">
              <span>Subtotal</span>
              <span>{{ totals().subtotal | number:'1.2-2' }} {{ currencyCode }}</span>
            </div>
            <div class="flex justify-between text-slate-600">
              <span class="pl-2">Traslados</span>
              <span>{{ totals().traslados | number:'1.2-2' }} {{ currencyCode }}</span>
            </div>
            <div class="flex justify-between text-slate-600">
              <span class="pl-2">Retenciones</span>
              <span>- {{ totals().retenciones | number:'1.2-2' }} {{ currencyCode }}</span>
            </div>
            @if ((planForm.get('discount')?.value ?? 0) > 0) {
              <div class="flex justify-between text-slate-600">
                <span>Descuento</span>
                <span>- {{ planForm.get('discount')?.value | number:'1.2-2' }} {{ currencyCode }}</span>
              </div>
            }
            <div class="flex justify-between font-semibold text-slate-900 pt-2 border-t border-slate-200">
              <span>Total</span>
              <span>{{ totals().total | number:'1.2-2' }} {{ currencyCode }}</span>
            </div>
          </div>
        </div>
      </div>
          </p-tabpanel>

          <p-tabpanel value="auditoria">
            @if (activeTab() === 'auditoria') {
              <app-entity-audit-panel
                entityType="Plan"
                [entityId]="editingPlanId()"
                entityLabel="plan"
              />
            }
          </p-tabpanel>
        </p-tabpanels>
      </p-tabs>

      <ng-template pTemplate="footer">
        <div class="!mt-4 flex justify-end gap-4 mt-1 !p-4 !-m-5 bg-gray-50 p-2 border-t border-slate-200">
          <p-button label="Cancelar" severity="secondary" (onClick)="cancel()" type="button" />
          <p-button
            [label]="isEditMode() ? 'Guardar plan' : 'Crear plan'"
            icon="pi pi-save"
            (onClick)="onSubmit()"
            [loading]="loading()"
            [disabled]="loading()"
            type="button"
          />
        </div>
      </ng-template>
    </p-card>
  </form>
</div>

<!-- Modal Agregar/Editar item -->
@if (lineModalOpen()) {
  <div class="fixed inset-0 z-50 flex items-center justify-center">
    <div
      class="absolute inset-0 bg-black transition-opacity duration-200"
      [class.bg-opacity-40]="lineModalShown()"
      [class.bg-opacity-0]="!lineModalShown()"
      (click)="closeLineModal()"
    ></div>
    <div
      class="relative z-10 flex max-h-[90vh] w-full max-w-2xl flex-col rounded-xl bg-white shadow-xl transform transition-all duration-200"
      [class.opacity-100]="lineModalShown()"
      [class.opacity-0]="!lineModalShown()"
      [class.scale-100]="lineModalShown()"
      [class.scale-95]="!lineModalShown()"
    >
      <div class="flex shrink-0 items-center justify-between gap-3 border-b border-slate-200 bg-slate-100 px-4 py-3">
        <h3 class="text-lg font-semibold text-slate-900">
          {{ lineModalMode() === 'create' ? 'Agregar item' : 'Editar item' }}
        </h3>
        <p-button icon="pi pi-times" [rounded]="true" [text]="true" severity="secondary" (onClick)="closeLineModal()" type="button" />
      </div>

      <div class="min-h-0 flex-1 overflow-y-auto p-6">
        <form [formGroup]="lineForm">
          <div class="flex flex-col gap-2 mb-4 pb-4 border-b border-slate-200">
            <div class="flex flex-wrap items-center gap-2">
              <span class="inline-flex">
                <p-selectButton
                  name="lineProductSource"
                  [options]="lineProductSourceOptions"
                  [ngModel]="lineProductSource()"
                  [ngModelOptions]="{ standalone: true }"
                  (onChange)="onLineProductSourceSelectChange($event)"
                  optionLabel="label"
                  optionValue="value"
                  [allowEmpty]="false"
                  [disabled]="lineModalMode() === 'edit'"
                >
                  <ng-template pTemplate="item" let-item>
                    <span class="flex items-center justify-center gap-2">
                      @if (lineProductSource() === item.value) {
                        <i class="pi pi-check text-blue-600 text-sm shrink-0"></i>
                      }
                      <span>{{ item.label }}</span>
                    </span>
                  </ng-template>
                </p-selectButton>
              </span>
              <i
                class="pi pi-info-circle text-slate-400 cursor-help text-lg shrink-0"
                [pTooltip]="lineProductSourceTooltip"
                [escape]="false"
                tooltipPosition="bottom"
              ></i>
            </div>
            <small class="text-slate-500">
              @if (lineProductSource() === 'CATALOG') {
                Busca un producto del catálogo para precargar datos e impuestos.
              } @else {
                Describe el concepto manualmente; no se vincula a un SKU del inventario.
              }
            </small>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div
              class="flex flex-col gap-1 md:col-span-2 relative rounded-md transition-opacity"
              [class.opacity-60]="lineProductSource() === 'OPEN'"
              [attr.inert]="lineProductSource() === 'OPEN' ? '' : null"
            >
              @if (lineProductSource() === 'OPEN') {
                <div class="absolute inset-0 z-20 rounded-md cursor-not-allowed bg-transparent" (click)="$event.preventDefault(); $event.stopPropagation()" tabindex="-1"></div>
              }
              <p-floatlabel class="w-full" variant="on">
                <p-autocomplete
                  id="productLine"
                  formControlName="productSelection"
                  [suggestions]="productOptions()"
                  (completeMethod)="onProductSearch($event)"
                  (onSelect)="onProductSelected($event.value)"
                  optionLabel="name"
                  placeholder="Busca producto (mín. 2 caracteres)"
                  fluid
                />
                <label for="productLine">Producto</label>
              </p-floatlabel>
            </div>

            <div class="flex flex-col gap-1 md:col-span-2">
              <p-floatlabel class="w-full" variant="on">
                <input pInputText
                  id="lineDescription"
                  formControlName="description"
                  [maxlength]="250"
                  fluid
                  placeholder="Descripción del item"
                  [invalid]="(lineForm.get('description')?.invalid ?? false) && (lineForm.get('description')?.touched ?? false)"
                />
                <label for="lineDescription">Descripción</label>
              </p-floatlabel>
              @if (lineForm.get('description')?.hasError('required') && lineForm.get('description')?.touched) {
                <small class="text-red-600">La descripción es obligatoria.</small>
              }
            </div>

            <div class="md:col-span-2 grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div class="flex flex-col gap-1">
                <p-floatlabel class="w-full" variant="on">
                  <p-inputNumber
                    id="itemQuantity"
                    formControlName="quantity"
                    [min]="0.0001"
                    mode="decimal"
                    [minFractionDigits]="0"
                    [maxFractionDigits]="4"
                    fluid
                    (onFocus)="selectAllOnFocus($event)"
                    (mouseup)="keepSelectionOnMouseUp($event)"
                    [invalid]="(lineForm.get('quantity')?.invalid ?? false) && (lineForm.get('quantity')?.touched ?? false)"
                  />
                  <label for="itemQuantity">Cantidad</label>
                </p-floatlabel>
                @if (lineForm.get('quantity')?.hasError('required') && lineForm.get('quantity')?.touched) {
                  <small class="text-red-600">La cantidad es obligatoria.</small>
                }
              </div>

              <div class="flex flex-col gap-1">
                <p-floatlabel class="w-full" variant="on">
                  <p-inputNumber
                    id="itemUnitPrice"
                    formControlName="unitPrice"
                    [min]="0"
                    mode="decimal"
                    locale="es-MX"
                    [minFractionDigits]="2"
                    [maxFractionDigits]="4"
                    [useGrouping]="true"
                    fluid
                    inputStyleClass="text-right"
                    (onFocus)="selectAllOnFocus($event)"
                    (mouseup)="keepSelectionOnMouseUp($event)"
                    [invalid]="(lineForm.get('unitPrice')?.invalid ?? false) && (lineForm.get('unitPrice')?.touched ?? false)"
                  />
                  <label for="itemUnitPrice">Precio unitario</label>
                </p-floatlabel>
                @if (lineForm.get('unitPrice')?.hasError('required') && lineForm.get('unitPrice')?.touched) {
                  <small class="text-red-600">El precio es obligatorio.</small>
                }
                @if (lineForm.get('unitPrice')?.hasError('min') && lineForm.get('unitPrice')?.touched) {
                  <small class="text-red-600">El precio debe ser mayor o igual a 0.</small>
                }
              </div>

              <div class="flex flex-col gap-1">
                <p-floatlabel class="w-full" variant="on">
                  <p-inputNumber
                    id="itemLineSubtotal"
                    [ngModel]="lineSubtotal()"
                    [ngModelOptions]="{standalone: true}"
                    mode="currency"
                    currency="USD"
                    locale="en-US"
                    [minFractionDigits]="2"
                    [maxFractionDigits]="4"
                    fluid
                    class="[&_input]:text-right"
                    [readonly]="true"
                    [disabled]="true"
                  />
                  <label for="itemLineSubtotal">Subtotal</label>
                </p-floatlabel>
                <small class="text-slate-500">Cantidad × precio unitario.</small>
              </div>
            </div>

            <div class="md:col-span-2 pt-3 mt-1 border-t border-slate-200">
              <button
                type="button"
                class="group inline-flex items-center gap-1.5 rounded px-1 py-1 -ml-1 text-xs text-slate-500 hover:text-slate-700 hover:bg-slate-50 transition-colors"
                (click)="lineFiscalDetailsExpanded.set(!lineFiscalDetailsExpanded())"
                [attr.aria-expanded]="lineFiscalDetailsExpanded()"
              >
                <i
                  class="pi text-[0.65rem] text-slate-400 group-hover:text-slate-600"
                  [class.pi-chevron-right]="!lineFiscalDetailsExpanded()"
                  [class.pi-chevron-down]="lineFiscalDetailsExpanded()"
                ></i>
                <span class="font-medium text-blue-500">Personalizar datos fiscales e impuestos</span>
              </button>
            </div>

            @if (lineFiscalDetailsExpanded()) {
              <div class="md:col-span-2 -mt-1">
                <span class="text-sm font-medium text-slate-800">Datos fiscales</span>
              </div>

              <div class="flex flex-col gap-1">
                <p-floatlabel class="w-full" variant="on">
                  <p-autocomplete
                    id="measureUnitLine"
                    formControlName="measureUnitSelection"
                    [suggestions]="measureUnitOptions()"
                    (completeMethod)="onMeasureUnitSearch($event)"
                    (onSelect)="onMeasureUnitSelected($event.value)"
                    optionLabel="name"
                    placeholder="Busca unidad (mín. 2 caracteres)"
                    fluid
                  />
                  <label for="measureUnitLine">Unidad de medida</label>
                </p-floatlabel>
              </div>

              <div class="flex flex-col gap-1">
                <p-floatlabel class="w-full" variant="on">
                  <p-select
                    formControlName="taxExempt"
                    inputId="taxExempt"
                    [options]="taxExemptOptions"
                    optionLabel="label"
                    optionValue="value"
                    class="w-full"
                  />
                  <label for="taxExempt">Exento de impuestos</label>
                </p-floatlabel>
              </div>

              <div class="flex flex-col gap-1">
                <p-floatlabel class="w-full" variant="on">
                  <p-autocomplete
                    id="taxCodeLine"
                    formControlName="taxProductCodeSelection"
                    [suggestions]="taxProductCodeOptions()"
                    (completeMethod)="onTaxProductCodeSearch($event)"
                    (onSelect)="onTaxProductCodeSelected($event.value)"
                    optionLabel="taxDescription"
                    placeholder="Busca código fiscal (mín. 2 caracteres)"
                    fluid
                  />
                  <label for="taxCodeLine">Código fiscal</label>
                </p-floatlabel>
              </div>

              <div class="flex flex-col gap-1">
                <p-floatlabel class="w-full" variant="on">
                  <p-select
                    formControlName="satObjetoImpId"
                    inputId="satObjetoImpLine"
                    [options]="satObjetoImpOptions()"
                    optionLabel="description"
                    optionValue="id"
                    class="w-full"
                    filter
                  />
                  <label for="satObjetoImpLine">Objeto de impuesto SAT</label>
                </p-floatlabel>
              </div>

              <div class="flex flex-col justify-center">
                <div class="flex items-center gap-2">
                  <p-checkbox
                    formControlName="taxInclusive"
                    inputId="taxInclusive"
                    [binary]="true"
                  />
                  <label for="taxInclusive" class="cursor-pointer select-none">Precio con impuestos incluidos</label>
                  <i
                    class="pi pi-info-circle text-slate-400 cursor-help"
                    [pTooltip]="taxInclusiveHelpText"
                    tooltipPosition="top"
                  ></i>
                </div>
              </div>
            }
          </div>
        </form>

        @if (lineFiscalDetailsExpanded()) {
          <!-- Impuestos del item -->
          <div class="mt-6 border-t border-slate-200 pt-4">
            <div class="flex items-center justify-between mb-3">
              <h4 class="text-sm font-semibold text-slate-700">Impuestos del item</h4>
              <p-button label="Agregar impuesto" icon="pi pi-plus" [text]="true" (onClick)="editLineTaxInModal(null)" type="button" />
            </div>
            @if (lineFormTaxes().length === 0) {
              <p class="text-sm text-slate-500">No hay impuestos. Agrega o selecciona un producto.</p>
            } @else {
              <div class="overflow-x-auto">
                <table class="w-full text-sm">
                  <thead>
                    <tr class="border-b border-slate-200">
                      <th class="text-left py-2 text-slate-600">Impuesto</th>
                      <th class="text-left py-2 text-slate-600">Tasa</th>
                      <th class="text-left py-2 text-slate-600">Monto</th>
                      <th class="text-left py-2 text-slate-600">Aplicación</th>
                      <th class="text-left py-2 text-slate-600">Tipo de cobro</th>
                      <th class="text-right py-2 text-slate-600">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    @for (t of lineFormTaxes(); track $index) {
                      <tr class="border-b border-slate-100">
                        <td class="py-2">{{ getTaxDisplayWithCode(t.taxCode) }}</td>
                        <td class="py-2">{{ t.taxRate ?? '-' }}</td>
                        <td class="py-2">{{ t.taxAmount | currency:'USD':'symbol':'1.2-2' ?? '-' }}</td>
                        <td class="py-2">{{ getTaxApplicationLabel(t.applicationType!) }}</td>
                        <td class="py-2">{{ getTaxCollectionTypeLabel(t.collectionType) }}</td>
                        <td class="py-2 text-right">
                          <p-button icon="pi pi-pen-to-square" [rounded]="true" [text]="true" size="small" severity="secondary" (onClick)="editLineTaxInModal($index)" type="button" />
                          <p-button icon="pi pi-trash" [rounded]="true" [text]="true" size="small" severity="danger" (onClick)="removeLineTaxInModal($index)" type="button" />
                        </td>
                      </tr>
                    }
                  </tbody>
                </table>
              </div>
            }
          </div>
        }
      </div>

      <div class="flex shrink-0 justify-end gap-3 border-t border-slate-200 bg-slate-100 px-4 py-3">
        <p-button label="Cancelar" severity="secondary" (onClick)="closeLineModal()" type="button" />
        <p-button
          [label]="lineModalMode() === 'create' ? 'Agregar' : 'Actualizar'"
          icon="pi pi-check"
          [loading]="lineSaving()"
          [disabled]="lineForm.invalid || lineSaving()"
          (onClick)="saveLine()"
          type="button"
        />
      </div>
    </div>
  </div>
}

<!-- Modal Impuesto de item -->
@if (lineModalOpen() && lineTaxModalOpen()) {
  <div class="fixed inset-0 z-50 flex items-center justify-center">
    <div class="absolute inset-0 bg-black/30" (click)="closeLineTaxModal()"></div>
    <div class="relative bg-white rounded-xl shadow-xl w-full max-w-md p-6 z-50">
      <div class="flex items-center justify-between mb-4">
        <h4 class="font-semibold text-slate-900">
          {{ editingLineTaxIndex() !== null ? 'Editar impuesto' : 'Agregar impuesto' }}
        </h4>
        <p-button icon="pi pi-times" [rounded]="true" [text]="true" severity="secondary" (onClick)="closeLineTaxModal()" type="button" />
      </div>
      <form [formGroup]="lineTaxForm">
        <div class="grid grid-cols-1 gap-4">
          <div class="flex flex-col gap-1">
            <p-floatlabel class="w-full" variant="on">
              <p-select
                formControlName="taxSelection"
                inputId="taxSelection"
                [options]="taxOptions()"
                optionLabel="displayName"
                placeholder="Selecciona impuesto"
                class="w-full"
                [invalid]="(lineTaxForm.get('taxSelection')?.invalid ?? false) && (lineTaxForm.get('taxSelection')?.touched ?? false)"
              />
              <label for="taxSelection">Impuesto</label>
            </p-floatlabel>
          </div>
          <div class="flex flex-col gap-1">
            <p-floatlabel class="w-full" variant="on">
              <p-select
                formControlName="applicationType"
                inputId="applicationType"
                [options]="taxApplicationOptions"
                optionLabel="label"
                optionValue="value"
                class="w-full"
              />
              <label for="applicationType">Tipo de aplicación</label>
            </p-floatlabel>
          </div>
          <div class="flex flex-col gap-1">
            <label class="block text-sm font-medium text-slate-700">Tipo de cobro</label>
            <p-selectButton
              formControlName="collectionType"
              [options]="taxCollectionTypeOptions"
              optionLabel="label"
              optionValue="value"
            >
              <ng-template pTemplate="item" let-item>
                <span class="flex items-center gap-2">
                  @if (lineTaxForm.get('collectionType')?.value === item.value) {
                    <i class="pi pi-check text-sm text-blue-500"></i>
                  }
                  <span>{{ item.label }}</span>
                </span>
              </ng-template>
            </p-selectButton>
            <small class="text-slate-500">Traslado o retención.</small>
          </div>
          <div class="flex flex-col gap-1">
            <p-floatlabel class="w-full" variant="on">
              <p-inputNumber
                id="taxRateLine"
                formControlName="taxRate"
                [min]="0"
                [max]="100"
                mode="decimal"
                [minFractionDigits]="0"
                [maxFractionDigits]="2"
                fluid
                placeholder="0"
              />
              <label for="taxRateLine">Tasa (%)</label>
            </p-floatlabel>
          </div>
          <div class="flex flex-col gap-1">
            <p-floatlabel class="w-full" variant="on">
              <p-inputNumber
                id="taxAmountLine"
                formControlName="taxAmount"
                [min]="0"
                mode="decimal"
                [minFractionDigits]="0"
                [maxFractionDigits]="2"
                fluid
                placeholder="0"
              />
              <label for="taxAmountLine">Monto</label>
            </p-floatlabel>
          </div>
          <div class="flex flex-col gap-1">
            <p-floatlabel class="w-full" variant="on">
              <p-inputNumber
                id="taxBaseLine"
                formControlName="taxBase"
                [min]="0"
                mode="decimal"
                [minFractionDigits]="0"
                [maxFractionDigits]="2"
                fluid
                placeholder="0"
              />
              <label for="taxBaseLine">Base</label>
            </p-floatlabel>
          </div>
        </div>
      </form>
      <div class="flex justify-end gap-3 mt-6">
        <p-button label="Cancelar" severity="secondary" (onClick)="closeLineTaxModal()" type="button" />
        <p-button label="Guardar" icon="pi pi-check" [disabled]="lineTaxForm.invalid" (onClick)="saveLineTaxInModal()" type="button" />
      </div>
    </div>
  </div>
}
